import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import localFont from "next/font/local";
import QuantaField from "./QuantaField";

const calSans = localFont({
  src: "../../fonts/CalSans-SemiBold.woff",
  variable: "--font-calSans",
});

// Where the app cards sit in the orbit stage: a triangle around the centre, so the rings and the
// pulses between apps stay visible instead of hiding behind the cards. Centring, pop-in and float
// each get their own wrapper because each one sets `transform`.
const SLOTS = [
  { left: "22%", top: "26%" },
  { left: "78%", top: "30%" },
  { left: "50%", top: "80%" },
];

const Hero = ({ content, apps = [] }) => {
  const artRef = useRef(null);
  return (
    <section className="relative overflow-hidden pb-10 pt-20 md:pt-32 h-1527">
      <picture className="pointer-events-none absolute inset-x-0 top-0 -z-10 block dark:hidden h-full">
        <Image
          width={1920}
          height={900}
          src="/images/gradient.jpg"
          alt="gradient"
          className="h-full w-full"
          priority
        />
      </picture>
      <picture className="pointer-events-none absolute inset-x-0 top-0 -z-10 hidden dark:block">
        <Image
          width={1920}
          height={900}
          src="/images/gradient_dark.jpg"
          alt="gradient dark"
          className="h-full w-full"
          priority
        />
      </picture>

      <div className="container h-full mx-auto">
        <div className="grid h-full items-center gap-4 md:grid-cols-12">
          <div className="col-span-6 flex h-full flex-col items-center justify-center py-10 md:items-start md:py-20 xl:col-span-4">
            <h1
              className={`${calSans.variable} bq-rise font-calSans text-jacarta-700 font-bold mb-6 text-center text-5xl dark:text-white md:text-left lg:text-6xl xl:text-7xl`}
            >
              {content?.heroTitle}
            </h1>
            <p className="bq-rise dark:text-jacarta-200 mb-8 text-center text-lg md:text-left" style={{ animationDelay: "120ms" }}>
              {content?.heroDescription}
            </p>
            <div className="bq-rise flex space-x-4" style={{ animationDelay: "240ms" }}>
              <Link
                href="#apps"
                className="bg-accent shadow-accent-volume hover:bg-accent-dark rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
              >
                See our apps
              </Link>
              <Link
                href="/contact"
                className="text-accent shadow-white-volume hover:bg-accent-dark hover:shadow-accent-volume rounded-full bg-white py-3 px-8 text-center font-semibold transition-all hover:text-white"
              >
                Contact us
              </Link>
            </div>
          </div>

          {/* <!-- Hero art: the apps in one orbital system (QuantaField draws the orbit) --> */}
          <div className="col-span-6 xl:col-span-8">
            <div ref={artRef} className="relative mx-auto h-[22rem] w-full max-w-[36rem] sm:h-[26rem] md:h-[30rem]">
              <QuantaField containerRef={artRef} className="absolute inset-0 h-full w-full" />
              {apps.filter((app) => app.icon?.url).slice(0, SLOTS.length).map((app, i) => (
                <div key={app.slug} className="absolute -translate-x-1/2 -translate-y-1/2" style={SLOTS[i]}>
                  <div className="bq-pop" style={{ animationDelay: `${450 + i * 140}ms` }}>
                  <div className="bq-float" style={{ animationDelay: `${-i * 1.7}s` }}>
                    <a
                      href="#apps"
                      className="dark:bg-jacarta-700/80 block rounded-3xl bg-white/80 p-3 text-center shadow-lg backdrop-blur transition-transform hover:-translate-y-1 md:p-4"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        data-quanta-anchor
                        src={`${app.icon.url}?w=256&h=256&fm=png`}
                        alt={app.icon.description || `${app.name} icon`}
                        width={112}
                        height={112}
                        className="mx-auto h-16 w-16 rounded-2xl sm:h-20 sm:w-20 md:h-24 md:w-24"
                      />
                      <span className="font-display text-jacarta-700 mt-2 block whitespace-nowrap text-sm font-semibold dark:text-white md:text-base">
                        {app.name}
                      </span>
                    </a>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
